import { Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
    callAPI,
    getLocalValue,
    LoacalVariables,
    Method
} from "../../commonFunctions/CommonFunctions";
import { Header, IHeaderActions } from "../../Components/Header/Header";
import { resetDataComponent } from "../../StateManagement/actions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";
import { openPopUp } from "../../Components/Dialog/OpenNewModal";
import { UpdateStatus } from "../components/UpdateStatus";
import { IMetaData } from "../../Components/Dialog/WSCommonDialog";
import config from '../../Config/clientConfig.json';
import { WSIcons } from "../../commonFunctions/iIcons";

const FilteredLoans: React.FC<any> = React.memo(() => {
    const dispatch = useDispatch();
    const { showMessage } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const statusParam = searchParams.get('status');
    const createdToday = searchParams.get('createdToday') === 'true';

    const filters = [];

    if (statusParam) {
        filters.push({ field: "status", operator: "eq", value: statusParam });
    }

    if (createdToday) {
        const today = new Date();
        const from = new Date(today.setHours(0, 0, 0, 0));
        const to = new Date(today.setHours(23, 59, 59, 999));

        filters.push({
            field: "createdAt",
            operator: "eq",
            value: {
                from,
                to,
            },
        });
    }

    const userType = getLocalValue(LoacalVariables.UserType);

    const [isPopupOpen, setPopupOpen] = React.useState(false); //for delete;
    const [selectedIdForDlt, setSelectedIdForDlt] = React.useState<any>(null);
    const [loanId, setLoanId] = React.useState(null);

    const [edit, setEdit] = useState(null);
    const [districtOptions, setDistrictOptions] = React.useState<any[]>([]);
    const [blockOptions, setBlockOptions] = React.useState<any[]>([]);
    const [cdsOptions, setCdsOptions] = React.useState<any[]>([]);

    const source = {
        url: "loans/search",
        page: 1,
        pageSize: 10,
        filter: filters,
        sort: []
    };

    useEffect(() => {
        if (userType === "admin") {
            fetchDistricts();
            fetchBlocks();
            fetchCDS();
        } else if (userType === "district") {
            const districtId = getLocalValue(LoacalVariables.District);
            if (districtId) {
                fetchBlocks(districtId);
            }
        }
    }, []);

    const fetchDistricts = async () => {
        try {
            const res = await callAPI("districts/getAll", Method.GET);
            setDistrictOptions(
                res?.data?.map((d: any) => ({ label: d.name, value: d._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch districts:", error);
            showMessage("Failed to load districts", "error");
        }
    };

    const fetchBlocks = async (districtId?: string) => {
        try {
            // setLoadingRegion(true);
            // const res = await callAPI(`blocks/byDistrict/${districtId}`, Method.GET);
            const res = await callAPI('blocks/getAll', Method.GET);

            setBlockOptions(
                res?.data?.map((b: any) => ({ label: b.name, value: b._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch blocks:", error);
            showMessage("Failed to load blocks", "error");
        } finally {
            // setLoadingRegion(false);
        }
    };

    const fetchCDS = async (blockId?: string) => {
        try {
            // const res = await callAPI(`cds/byBlock/${blockId}`, Method.GET);
            const res = await callAPI('cds/getAll', Method.GET);

            setCdsOptions(
                res?.data.map((c: any) => ({ label: c.name, value: c._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch CDS:", error);
            showMessage("Failed to load CDS", "error");
        }
    };


    // Determine limited actions based on statusParam and userType
    let limitedActions: string[];

    switch (statusParam) {
        case 'sanctioned':
            switch (userType) {
                case 'admin':
                case 'district':
                    limitedActions = ["Complete", "View", "Activity Logs"];
                    break;
                case 'block':
                case 'cds':
                    limitedActions = ["View"];
                    break;
                default:
                    limitedActions = [];
            }
            break;

        case 'disbursed':
            switch (userType) {
                case 'admin':
                case 'district':
                    limitedActions = ["View", "Activity Logs", "View Repayments"];
                    break;
                case 'block':
                    limitedActions = ["View", "Add Repayment", "View Repayments"];
                    break;
                case 'cds':
                    limitedActions = ["View"];
                    break;
                default:
                    limitedActions = [];
            }
            break;
        case 'completed':
            switch (userType) {
                case 'admin':
                case 'district':
                    limitedActions = ["View", "Activity Logs", "View Repayments"];
                    break;
                case 'block':
                case 'cds':
                    limitedActions = ["View"];
                    break;
                default:
                    limitedActions = [];
            }
            break;

        case 'pending':
        default:
            switch (userType) {
                case 'admin':
                case 'district':
                    limitedActions = ["Sanction", "Delete", "View", "Activity Logs"];
                    break;
                case 'block':
                    limitedActions = ["Edit", "Delete", "View"];
                    break;
                case 'cds':
                    limitedActions = ["View"];
                    break;
                default:
                    limitedActions = [];
            }
            break;
    }

    // edit / delete or perform other actions.
    const handleAction = async (action: string, row: any) => {
        if (action === "Edit") {
            setEdit(row);
            navigate(`/loan/request/${row?._id}`);
        } else if (action === "Delete") {
            setPopupOpen(true);
            setSelectedIdForDlt(row?._id);
        } else if (action === "Activity Logs") {
            navigate(`/loan/activityLogs/${row?._id}`);
        } else if (action === "View") {
            navigate(`/loan/detail/${row?._id}`);
        } else if (action === 'Sanction') {
            navigate(`/loan/request/sanction/${row?._id}`);
        } else if (action === 'Disburse') {
            navigate(`/loan/request/disburse/${row?._id}`)
        } else if (action === 'Add Repayment') {
            navigate(`/loan/repayment/request/create/${row?._id}`)
        } else if (action === 'View Repayments') {
            navigate(`/loan/repayment/requests/list/filter/${row?._id}`)
        }
    };


    const handleStatus = async (data: any) => {
        try {
            const response: any = await saveData(data);
            setLoanId(null);

            if (response && response?.message) {
                dispatch(resetDataComponent("loanGrid"));
                showMessage(
                    response.message,
                    response.success === true ? "success" : "error"
                );
            }
        } catch (e: any) {
            showMessage(e.response?.data?.message || "An unexpected error occurred", "error");
        } finally {
            setLoanId(null);
        }
    };

    const metadataStatus: IMetaData = {
        title: "Update Status",
        hideActions: false,
        content: (props: any, row: any) => <UpdateStatus {...props} config={config.loanStatusConfig.staticOptionData} />,
        actions: [
            {
                label: "Cancel",
                className: "ws-cancel",
                type: "button"
            },
            {
                label: "Submit",
                onClick: handleStatus,
                type: "submit",
                autoFocus: true
            }
        ],
        isDragable: true
    };

    const saveData = async (data: any) => {
        const request: any = {
            status: data?.newStatus,
        };

        const response: any = await callAPI(
            `loans/update/status/${loanId}`,
            Method.PATCH,
            request
        );
        
        return response;
    };

    const handleCloseDialog = (data?: any) => {
        setLoanId(null);
    };

    React.useEffect(() => {
        if (loanId) {
            openPopUp(metadataStatus, handleStatus, "40rem", handleCloseDialog);
        }
    }, [loanId]);

    const handleDelete = () => {
        dispatch(resetDataComponent("loanGrid"));
        showMessage("Loan deleted successfully!", "success");
    };

    const handleClick = () => {
        navigate('/loan/request')
    };


    const columns: IColumns[] = [
        {
            header: "Loan ID",
            fieldName: "loanId",
            tooltip: true,
            sortable: true
        },
        {
            header: "Applicant Name",
            fieldName: "applicantName",
            tooltip: true,
            sortable: true
        },
        {
            header: "Enterprise Name",
            fieldName: "enterpriseName",
            tooltip: true,
            sortable: true
        },
        {
            header: "Mobile Number",
            fieldName: "mobile",
            tooltip: true
        },

        {
            header: "Applied Amount",
            fieldName: "appliedAmount",
            tooltip: true,
            sortable: true,
            type: "currency"
        },

        {
            header: userType === 'district' || userType === "admin" ? "Block" : "CDS",
            fieldName: userType === 'district' || userType === "admin" ? "block.name" : "cds.name",
            tooltip: true,
            type: "default"
        },

        {
            header: "Status",
            fieldName: "status",
            tooltip: true,
            type: "status"
        },
        {
            header: "Actions",
            fieldName: "actions",
            type: "action",
            actions: limitedActions,
            actionCallback: (action, row) => handleAction(action, row)
        }
    ];

    const filterFields = [
        { type: "text", fieldName: "loanId", label: "Loan ID" },
        { type: "text", fieldName: "applicantName", label: "Applicant Name", operator: "regex" },
        { type: "text", fieldName: "secondApplicantName", label: "Second Applicant Name" },
        { type: "text", fieldName: "mobile", label: "Mobile Number", operator: "regex" },
        { type: "text", fieldName: "whatsappNumber", label: "WhatsApp Number", operator: "regex" },
        { type: "text", fieldName: "enterpriseName", label: "Enterprise Name", operator: "regex" },
        { type: "text", fieldName: "referenceNumber", label: "Reference Number", operator: "eq" },

        // { type: "dropdown", fieldName: "loanType", label: "Loan Type", options: loanTypeOptions },
        // { type: "dropdown", fieldName: "district", label: "District", options: districtOptions },
        { type: "dropdown", fieldName: "block", label: "Block", options: blockOptions },
        { type: "dropdown", fieldName: "cds", label: "CDS", options: cdsOptions },

        // { type: "date", fieldName: "sanctionedDate", label: "Sanctioned Date" },
        // { type: "date", fieldName: "fundTransferDate", label: "Fund Transfer Date" },

        // {
        //     type: "dropdown", fieldName: "status", label: "Status", options: [
        //         { label: "Pending", value: "pending" },
        //         // { label: "Approved", value: "approved" },
        //         { label: "Rejected", value: "rejected" },
        //         // { label: "Sanctioned", value: "sanctioned" }
        //     ]
        // },
    ];


    const action: IHeaderActions = {
        type: "button",
        text: "+ New Loan Request",
        size: "medium",
        onClick: handleClick,
        variant: "contained",
        color: "primary",
        sx: {
            backgroundColor: "#4DD26B",
            textTransform: "none",
            "&:hover": { backgroundColor: "#b94886" }
        }
    };

    return (
        <>
            <Helmet>
                <title>All Loans | Willsoar Technologies</title>
                <meta
                    name="description"
                    content="Manage all loans in one place. View, add, or edit loan information easily."
                />
            </Helmet>
            <Grid p={2}>
                {userType === "block" ? <Header
                    type="new"
                    title="Loans"
                    id={"loanHeader"}
                    action={action}
                    // icon={<WSIcons.Document fontSize="medium" color="#7D79C2" />}
                /> : <Header
                    type="new"
                    title="Loans"
                    id={"loanHeader"}
                    // icon={<WSIcons.Document fontSize="medium" color="#7D79C2" />}
                />}

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <WSDataGrid
                            id="loanGrid"
                            columns={columns}
                            source={source}
                            filterFields={filterFields}
                            searchBox={{ fieldName: "mobile", type: "number" }}
                            placeholder={"Search by Mobile Number"}
                            exportCsv
                            buttonStyle="Sigma"
                        />
                    </Grid>
                </Grid>

                {isPopupOpen && (
                    <ConfirmationPopup
                        apiUrl={"loans/delete"}
                        id={selectedIdForDlt}
                        confirmMessage="Are you sure you want to delete this loan?"
                        subtitle="This action cannot be undone"
                        open={isPopupOpen}
                        onClose={() => setPopupOpen(false)}
                        onConfirm={handleDelete}
                        permanent
                    />
                )}
            </Grid>
        </>
    );
});


export default FilteredLoans;